// Price-alert rule: nudge the user when the median sold comparable sits at
// least `threshold` above the price they set on a draft. Keeps the
// comparison local (no new eBay calls) by reusing the `price_research` the
// Deep Research step already stored on each draft. `avg` is the AI's median
// comp; `high` is the top outlier (surfaced in the banner so the user can
// see headroom). Threshold is user-configurable via EbaySettings and
// persisted in `localStorage` so every surface (editor banner, library chip,
// hint text) stays in sync.
const DEFAULT_THRESHOLD_PCT = 50;
export const PRICE_ALERT_SETTING_KEY = "snaplist.priceAlertThresholdPct";

/** Read the user's configured threshold %, clamped to [10, 500]. */
export function getPriceAlertThresholdPct() {
  try {
    const raw = localStorage.getItem(PRICE_ALERT_SETTING_KEY);
    if (raw == null) return DEFAULT_THRESHOLD_PCT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_THRESHOLD_PCT;
    return Math.min(500, Math.max(10, Math.round(n)));
  } catch {
    return DEFAULT_THRESHOLD_PCT;
  }
}

/**
 * Returns null if the draft doesn't qualify (missing comps, no price, or
 * already published/sold — those can't be re-priced in-app). Otherwise
 * returns the data needed to render the nudge.
 */
export function computePriceAlert(draft) {
  if (!draft) return null;
  // Alerts only make sense for drafts the user can still re-price before
  // publishing. Skip anything already live or sold.
  const status = draft?.ebay?.status;
  if (status === "PUBLISHED" || status === "SOLD") return null;

  const pr = draft?.price_research;
  const median = Number(pr?.avg);
  const high = Number(pr?.high);
  const price = Number(draft?.price);
  if (!Number.isFinite(median) || median <= 0) return null;
  if (!Number.isFinite(price) || price <= 0) return null;

  const uplift = (median - price) / price;
  const thresholdPct = getPriceAlertThresholdPct();
  if (uplift < thresholdPct / 100) return null;

  return {
    median,
    high: Number.isFinite(high) && high > 0 ? high : null,
    price,
    uplift,
    upliftPct: Math.round(uplift * 100),
    // Round the suggestion to a clean 2-decimal value.
    suggested: Math.round(median * 100) / 100,
  };
}
